// API client for the Verso admin console.
//
// Simpler than the org-tier dashboard's api.js — admin API is always
// same-origin (admin.verso-cloud.io/api/*, both served by CF), so no
// cross-origin cookie forwarding is required. No X-Verso-Stale handling
// either — admin routes don't proxy to on-prem agents.

const API_BASE = import.meta.env.VITE_API_BASE || window.location.origin;
const REQUEST_TIMEOUT_MS = 10_000;

let sessionToken = null;

export function setSessionToken(token) {
  sessionToken = token;
}

export function getSessionToken() {
  return sessionToken;
}

async function request(method, path, { params, body } = {}) {
  const url = new URL(`/api/v1${path}`, API_BASE);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value != null) url.searchParams.set(key, value);
    }
  }

  const options = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };

  if (sessionToken) {
    options.headers['Authorization'] = `Bearer ${sessionToken}`;
  }

  if (body) {
    options.body = JSON.stringify(body);
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  options.signal = controller.signal;

  let res;
  try {
    res = await fetch(url, options);
  } catch (err) {
    clearTimeout(timeout);
    if (err.name === 'AbortError') {
      const timeoutErr = new Error('Request timed out — please try again');
      timeoutErr.status = 0;
      throw timeoutErr;
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error(errBody.error || `API ${method} ${path} failed (${res.status})`);
    err.status = res.status;
    err.body = errBody;
    throw err;
  }

  return res.status === 204 ? null : await res.json();
}

export const api = {
  get: (path, params) => request('GET', path, { params }),
  post: (path, body, params) => request('POST', path, { params, body }),
  put: (path, body, params) => request('PUT', path, { params, body }),
  delete: (path, body, params) => request('DELETE', path, { params, body }),
};
